import { useSearchParams } from "react-router";

import { PageHeader } from "@/components/page-header";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CancellationsPanel } from "@/screens/cancellations.screen";
import { ChangeRequestsPanel } from "@/screens/change-requests.screen";
import { RefundsPanel } from "@/screens/refund-requests.screen";

const TABS = ["cancellations", "change-requests", "refunds"] as const;
type ApprovalsTab = (typeof TABS)[number];

function isTab(value: string | null): value is ApprovalsTab {
  return value !== null && (TABS as readonly string[]).includes(value);
}

/**
 * Approvals inbox — consolidates the carrier's decision queues (cancellations,
 * change requests, refund lines) into a single tabbed screen. Each tab renders
 * a self-contained panel that owns its own data + mutations. The active tab is
 * synced to the `?tab=` query param so queues stay deep-linkable and survive a
 * refresh (the old per-queue routes redirect here with the matching `tab`).
 */
export default function ApprovalsScreen() {
  const [params, setParams] = useSearchParams();
  const rawTab = params.get("tab");
  const tab =
    rawTab === "pickup-refunds"
      ? "refunds"
      : isTab(rawTab)
        ? rawTab
        : "cancellations";

  return (
    <div className="carrier-page">
      <PageHeader title="Approvals" />

      <Tabs
        value={tab}
        onValueChange={(value) => setParams({ tab: value }, { replace: true })}
      >
        <div className="-mx-1 overflow-x-auto px-1 pb-1">
          <TabsList
            aria-label="Approval categories"
            size="touch"
            className="min-w-max"
          >
            <TabsTrigger className="px-3" value="cancellations">
              Cancellations
            </TabsTrigger>
            <TabsTrigger className="px-3" value="change-requests">
              Change requests
            </TabsTrigger>
            <TabsTrigger className="px-3" value="refunds">
              Refund approvals
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="cancellations">
          <CancellationsPanel />
        </TabsContent>
        <TabsContent value="change-requests">
          <ChangeRequestsPanel />
        </TabsContent>
        <TabsContent value="refunds">
          <RefundsPanel />
        </TabsContent>
      </Tabs>
    </div>
  );
}
